import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IonPage,
  IonContent,
  IonInput,
  IonIcon,
  IonSpinner,
  IonToast,
} from '@ionic/react';
import { eyeOutline, eyeOffOutline, chatbubbleEllipsesOutline } from 'ionicons/icons';
import { ImpactStyle } from '@capacitor/haptics';
import api from '../../lib/api';
import { useFeatureFlags } from '@shared/flags/FeatureFlagProvider';
import { useCapacitorPlatform } from '@shared/hooks/useCapacitorPlatform';
import { handleLoginSuccess } from '../../utils/handleLoginSuccess';
import ProExternalLinkSheet from '../../components/pro/bottom-sheet/ProExternalLinkSheet';
import ProForgotPasswordSheet from '../../components/pro/bottom-sheet/ProForgotPasswordSheet';
import logomark from '../../assets/capitol-commons-logomark.svg';
import './ProLoginPage.css';

const ProLoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [forgotSheetOpen, setForgotSheetOpen] = useState(false);
  const emailInputRef = useRef<HTMLIonInputElement>(null);
  const passwordInputRef = useRef<HTMLIonInputElement>(null);
  const [linkSheet, setLinkSheet] = useState<{ open: boolean; title: string; url: string }>({
    open: false,
    title: '',
    url: '',
  });

  const { t } = useTranslation('pro');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { identifyUser, flags } = useFeatureFlags();
  const { isNative, applyStatusBar, triggerHaptic } = useCapacitorPlatform();
  const returnUrl = searchParams.get('returnUrl') || '/pro/home';

  useEffect(() => {
    if (!isNative) return;
    void applyStatusBar({ iconStyle: 'light', backgroundColor: '#4D3161' });
  }, [isNative, applyStatusBar]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    await triggerHaptic(ImpactStyle.Medium);
    try {
      // iOS Autofill can update IonInput's native field without immediately
      // dispatching the Ionic event that keeps React state current. Read the
      // native fields at submit time so the visible values are the submitted
      // values on both iOS and Android.
      const [nativeEmail, nativePassword] = await Promise.all([
        emailInputRef.current?.getInputElement().then((input) => input.value) ?? Promise.resolve(email),
        passwordInputRef.current?.getInputElement().then((input) => input.value) ?? Promise.resolve(password),
      ]);
      const identifier = nativeEmail.trim();
      const res = await api.post('/auth/login', { identifier, password: nativePassword });
      if (res.data?.success && res.data?.data?.token) {
        handleLoginSuccess({
          data: res.data.data,
          navigate,
          identifyUser,
          returnUrl,
          citizenEnabled: flags.citizen_enabled,
        });
      } else {
        // Surface a server-provided message (e.g. "Account deactivated…")
        // even when the response resolved without a token.
        setError(res.data?.error || 'Invalid response from server.');
      }
    } catch (err: unknown) {
      const apiError =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(apiError || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = !email || !password || loading;

  return (
    <IonPage className="pro-login">
      <IonContent fullscreen scrollY={true}>
        <div className="pro-login__canvas">
          <div className="pro-login__hero">
            <img src={logomark} alt="" className="pro-login__logomark" aria-hidden="true" />
            <h1 className="pro-login__wordmark">Capitol Commons</h1>
          </div>

          <div className="pro-login__card">
            <form onSubmit={handleSubmit} autoComplete="on" noValidate>
              <div className="pro-login__field">
                <label className="pro-login__field-label" htmlFor="pro-login-email">
                  Email / Username
                </label>
                <IonInput
                  ref={emailInputRef}
                  id="pro-login-email"
                  className="pro-login__input"
                  type="email"
                  inputmode="email"
                  autocomplete="email"
                  placeholder="Email or username"
                  value={email}
                  onIonInput={(e) => setEmail(e.detail.value ?? '')}
                  required
                />
              </div>
              <div className="pro-login__field">
                <label className="pro-login__field-label" htmlFor="pro-login-password">
                  Password
                </label>
                <IonInput
                  ref={passwordInputRef}
                  id="pro-login-password"
                  className="pro-login__input"
                  type={showPassword ? 'text' : 'password'}
                  autocomplete="current-password"
                  placeholder="Password"
                  value={password}
                  onIonInput={(e) => setPassword(e.detail.value ?? '')}
                  required
                >
                  <button
                    slot="end"
                    type="button"
                    className="pro-login__password-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <IonIcon icon={showPassword ? eyeOffOutline : eyeOutline} />
                  </button>
                </IonInput>
              </div>

              {error && (
                <div className="pro-login__error" role="alert" aria-live="assertive">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="pro-login__submit"
                disabled={isDisabled}
              >
                {loading ? <IonSpinner name="crescent" className="pro-login__spinner" /> : 'Sign in'}
              </button>
            </form>

            <div className="pro-login__links">
              <button
                type="button"
                className="pro-login__link"
                onClick={() => setForgotSheetOpen(true)}
              >
                Forgot your password?
              </button>
              <button
                type="button"
                className="pro-login__link pro-login__link--support"
                onClick={() =>
                  navigate('/pro/contact-support', {
                    state: { defaultEmail: email },
                  })
                }
              >
                <IonIcon icon={chatbubbleEllipsesOutline} />
                Contact Support
              </button>
            </div>
          </div>

          <p className="pro-login__legal">
            By signing in, you agree to our{' '}
            <button
              type="button"
              className="pro-login__legal-link"
              onClick={() =>
                setLinkSheet({ open: true, title: 'Terms of Use', url: 'https://capitolcommons.ai/terms' })
              }
            >
              Terms of Use
            </button>{' '}
            and{' '}
            <button
              type="button"
              className="pro-login__legal-link"
              onClick={() =>
                setLinkSheet({ open: true, title: 'Privacy Policy', url: 'https://capitolcommons.ai/privacy' })
              }
            >
              Privacy Policy
            </button>
            .
          </p>

          <p className="pro-login__legal">
            Capitol Commons is an independent application and is&nbsp;not&nbsp;affiliated with or
            endorsed by any government entity.
          </p>
        </div>

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2200}
          position="bottom"
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>

      {/*
       * IonModal-based sheets must be siblings of IonContent (still inside
       * IonPage), not children — Ionic's sheet-modal lifecycle gets confused
       * when the modal is declared inside IonContent's shadow slot, and the
       * sheet silently fails to present. Same pattern used on the dashboard
       * cards (see ProCalendarCard for precedent).
       */}
      <ProExternalLinkSheet
        isOpen={linkSheet.open}
        onDismiss={() => setLinkSheet((prev) => ({ ...prev, open: false }))}
        title={linkSheet.title}
        url={linkSheet.url}
      />
      <ProForgotPasswordSheet
        isOpen={forgotSheetOpen}
        onDismiss={() => setForgotSheetOpen(false)}
        defaultEmail={email}
        onSuccess={() => setToast(t('bottomSheet.forgotPasswordSuccessToast'))}
      />
    </IonPage>
  );
};

export default ProLoginPage;
